import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  Building2, Plus, Pencil, Trash2, X, Users, ToggleLeft, ToggleRight,
  AlertTriangle, Sparkles, RefreshCw, Pause, Play, KeyRound, UserCog,
  Database, BarChart3, Check, ChevronRight, Package as PackageIcon,
  LayoutGrid, FileText,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiv", badgeCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { value: "suspended", label: "Gesperrt", badgeCls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "cancelled", label: "Gekündigt", badgeCls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
];

export default function SuperAdminTenants() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [flagsModalFor, setFlagsModalFor] = useState(null);
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [manageFor, setManageFor] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([api.get("/tenants"), api.get("/packages")]);
      setTenants(tRes.data);
      setPackages(pRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function suspendOrReactivate(t) {
    const target = t.status === "suspended" ? "reactivate" : "suspend";
    const verb = target === "suspend" ? "sperren" : "reaktivieren";
    if (t.is_legacy) {
      toast.error("Der Standard-Mandant kann nicht gesperrt werden");
      return;
    }
    if (!window.confirm(`Mandant "${t.name}" wirklich ${verb}?`)) return;
    try {
      await api.post(`/tenants/${t.id}/${target}`);
      toast.success(target === "suspend" ? "Mandant gesperrt" : "Mandant reaktiviert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${verb} fehlgeschlagen`);
    }
  }

  async function removeTenant(t) {
    if (t.is_legacy) {
      toast.error("Der Standard-Mandant kann nicht gelöscht werden");
      return;
    }
    if (!window.confirm(`Mandant "${t.name}" wirklich unwiderruflich löschen?\n\nAlle ${t.user_count || 0} Benutzer verlieren den Zugang.`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Mandant gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  const stats = useMemo(() => ({
    total: tenants.length,
    active: tenants.filter(t => t.status === "active").length,
    suspended: tenants.filter(t => t.status === "suspended").length,
    users: tenants.reduce((s, t) => s + (t.user_count || 0), 0),
  }), [tenants]);

  // Gate: only super-admins (evaluated after hooks to satisfy rules-of-hooks)
  if (user && !user.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6 md:p-8" data-testid="super-admin-tenants-page">
      <ModuleHint moduleKey="tenants" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-400"/>
            Mandanten-Verwaltung
            <span className="ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Super-Admin</span>
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Plattform-weite Verwaltung aller Kunden dieser SaaS-Instanz.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setOnboardResult(null); setShowOnboard(true); }}
            data-testid="tenant-onboard-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded">
            <Sparkles className="w-4 h-4"/> Kunde onboarden
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="tenant-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-sm font-medium rounded">
            <Plus className="w-4 h-4"/> Manuell anlegen
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <StatCard label="Mandanten" value={stats.total} icon={Building2} testid="stat-total"/>
        <StatCard label="Aktiv" value={stats.active} icon={ToggleRight} color="emerald" testid="stat-active"/>
        <StatCard label="Gesperrt" value={stats.suspended} icon={AlertTriangle} color="amber" testid="stat-suspended"/>
        <StatCard label="Benutzer gesamt" value={stats.users} icon={Users} color="blue" testid="stat-users"/>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Laden…</div>
        ) : tenants.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-zinc-700"/>
            Noch keine Mandanten angelegt.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-center">Plan</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Benutzer</th>
              <th className="px-4 py-3 text-left">Erstellt</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{tenants.map(t => {
              const statusMeta = STATUS_OPTIONS.find(s => s.value === t.status) || STATUS_OPTIONS[0];
              return (
                <tr key={t.id} data-testid={`tenant-row-${t.slug}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-zinc-100 inline-flex items-center gap-2">
                      {t.name}
                      {t.is_legacy && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/30">Legacy</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-zinc-400 text-xs">{t.slug}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 uppercase tracking-wider font-semibold">{t.plan}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded font-semibold uppercase border ${statusMeta.badgeCls}`}>
                      {statusMeta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{t.user_count ?? 0}</td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs font-mono">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => setManageFor(t)} data-testid={`tenant-manage-${t.slug}`}
                        title="Verwalten (Übersicht / Module / Aktionen)"
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                        <UserCog className="w-3.5 h-3.5"/> Verwalten
                      </button>
                      <button onClick={() => setFlagsModalFor(t)} data-testid={`tenant-flags-${t.slug}`}
                        title="Feature-Flags"
                        className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300">
                        <Sparkles className="w-3.5 h-3.5"/> Flags
                      </button>
                      {!t.is_legacy && (
                        <button onClick={() => suspendOrReactivate(t)}
                          data-testid={`tenant-suspend-${t.slug}`}
                          title={t.status === "suspended" ? "Reaktivieren" : "Sperren"}
                          className={`inline-flex items-center ${t.status === "suspended" ? "text-emerald-400 hover:text-emerald-300" : "text-amber-400 hover:text-amber-300"}`}>
                          {t.status === "suspended" ? <Play className="w-3.5 h-3.5"/> : <Pause className="w-3.5 h-3.5"/>}
                        </button>
                      )}
                      <button onClick={() => { setEditing(t); setShowForm(true); }} data-testid={`tenant-edit-${t.slug}`}
                        title="Bearbeiten"
                        className="inline-flex items-center text-zinc-400 hover:text-zinc-100">
                        <Pencil className="w-3.5 h-3.5"/>
                      </button>
                      {!t.is_legacy && (
                        <button onClick={() => removeTenant(t)} data-testid={`tenant-delete-${t.slug}`}
                          title="Löschen"
                          className="inline-flex items-center text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <TenantForm
          editing={editing}
          packages={packages}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {flagsModalFor && (
        <TenantFeatureFlagsModal
          tenant={flagsModalFor}
          onClose={() => setFlagsModalFor(null)}
        />
      )}

      {showOnboard && (
        <OnboardingWizard
          packages={packages}
          onClose={() => { setShowOnboard(false); }}
          onSuccess={(res) => { setOnboardResult(res); load(); }}
        />
      )}

      {onboardResult && !showOnboard && (
        <OnboardSuccessModal
          result={onboardResult}
          onClose={() => setOnboardResult(null)}
        />
      )}

      {manageFor && (
        <TenantManagementModal
          tenant={manageFor}
          onClose={() => setManageFor(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "zinc", testid }) {
  const colorMap = {
    zinc: "text-zinc-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    blue: "text-blue-400",
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${colorMap[color] || colorMap.zinc}`}/>
      </div>
      <div className="text-2xl font-display font-semibold text-zinc-50">{value}</div>
    </div>
  );
}

function TenantForm({ editing, packages, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => editing ? {
    slug: editing.slug,
    name: editing.name,
    package_id: editing.package_id || "",
    plan: editing.plan || "trial",
    status: editing.status || "active",
    branding: editing.branding || {},
    billing: editing.billing || {},
  } : {
    slug: "",
    name: "",
    package_id: "",
    plan: "trial",
    status: "active",
    branding: {},
    billing: {},
  });
  const [saving, setSaving] = useState(false);

  const selectedPkg = packages.find(p => p.id === form.package_id);

  async function save() {
    if (!form.name?.trim()) { toast.error("Firmenname pflicht"); return; }
    if (!isEdit) {
      if (!form.slug?.trim()) { toast.error("Slug pflicht"); return; }
      if (!/^[a-z0-9_-]+$/i.test(form.slug)) { toast.error("Slug darf nur Buchstaben, Zahlen, - und _ enthalten"); return; }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/tenants/${editing.id}`, {
          name: form.name, package_id: form.package_id || "",
          status: form.status,
          branding: form.branding, billing: form.billing,
        });
        toast.success("Mandant aktualisiert");
      } else {
        await api.post("/tenants", form);
        toast.success("Mandant angelegt");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="tenant-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-400"/>
            {isEdit ? "Mandant bearbeiten" : "Neuer Mandant"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="tenant-form-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Firmenname *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="tenant-name"
                placeholder="z.B. Müller Transport GmbH"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Slug {isEdit && <span className="text-zinc-600">(nicht änderbar)</span>} *</label>
              <input value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-") })}
                disabled={isEdit}
                data-testid="tenant-slug"
                placeholder="mueller-transport"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono disabled:opacity-50"/>
              <div className="text-[10px] text-zinc-500 mt-1">URL-freundliche Kennung, nur Kleinbuchstaben/Zahlen/-/_.</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Abo-Paket *</label>
              <select value={form.package_id}
                onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                data-testid="tenant-package"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="">— kein Paket —</option>
                {packages.filter(p => p.is_active !== false).map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} · €{p.price_monthly_eur}/Monat
                  </option>
                ))}
              </select>
              {selectedPkg && (
                <div className="text-[10px] text-emerald-400 mt-1">
                  {selectedPkg.enabled_modules?.length || 0} Module, max.{" "}
                  {selectedPkg.max_users || "∞"} Nutzer, max.{" "}
                  {selectedPkg.max_vehicles || "∞"} Fahrzeuge
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="tenant-status"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Branding (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500">Primärfarbe (Hex)</label>
                <input value={form.branding?.primary_color || ""}
                  onChange={(e) => setForm({ ...form, branding: { ...form.branding, primary_color: e.target.value } })}
                  placeholder="#2563EB"
                  data-testid="tenant-branding-color"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100 font-mono"/>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">App-Name (für Browser-Titel)</label>
                <input value={form.branding?.app_name || ""}
                  onChange={(e) => setForm({ ...form, branding: { ...form.branding, app_name: e.target.value } })}
                  placeholder="z.B. Müller ERP"
                  data-testid="tenant-branding-appname"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100"/>
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              Logo wird pro Mandant unter Einstellungen → Firma hochgeladen.
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Billing (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500">Kontakt-E-Mail</label>
                <input type="email" value={form.billing?.email || ""}
                  onChange={(e) => setForm({ ...form, billing: { ...form.billing, email: e.target.value } })}
                  placeholder="billing@firma.at"
                  data-testid="tenant-billing-email"
                  className="w-full mt-0.5 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100"/>
              </div>
            </div>
          </div>

          {isEdit && editing.is_legacy && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 text-xs text-blue-200">
              <strong>Legacy-Mandant</strong>: Dies ist der automatisch erstellte Standard-Mandant,
              in dem alle bestehenden Daten leben. Slug kann nicht geändert werden, Löschen ist deaktiviert.
            </div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="tenant-save-btn"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : (isEdit ? "Speichern" : "Mandant anlegen")}
          </button>
        </div>
      </div>
    </div>
  );
}

function TenantFeatureFlagsModal({ tenant, onClose }) {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/tenants/${tenant.id}/feature-flags`);
      setData(data);
      setOverrides(data.overrides || {});
    } catch (e) { toast.error("Laden fehlgeschlagen"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function toggle(key) {
    setOverrides(o => ({ ...o, [key]: !(o[key] ?? data.state[key]) }));
  }
  function clearOverride(key) {
    setOverrides(o => { const n = { ...o }; delete n[key]; return n; });
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.id}/feature-flags`, { state: overrides });
      toast.success("Feature-Flags gespeichert");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  if (!data) return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-zinc-400 text-sm">Lade…</div>
    </div>
  );

  // Group registry by category
  const grouped = {};
  for (const f of data.registry) {
    const cat = f.category || "Sonstige";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="tenant-flags-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400"/> Feature-Flags
            </h3>
            <div className="text-xs text-zinc-500 mt-0.5">
              {tenant.name} · <span className="font-mono">{tenant.slug}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="tenant-flags-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 rounded p-3">
            <strong className="text-zinc-300">So funktioniert's:</strong> Klick auf eine Flag setzt einen mandanten-spezifischen Override.
            Ohne Override gilt der <em>globale</em> Standardwert (gelb markiert). Override entfernen: RefreshCw-Icon klicken.
          </div>

          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">{cat}</h4>
              <div className="space-y-2">
                {items.map(f => {
                  const hasOverride = Object.prototype.hasOwnProperty.call(overrides, f.key);
                  const effective = hasOverride ? overrides[f.key] : data.state[f.key];
                  return (
                    <div key={f.key} data-testid={`flag-row-${f.key}`}
                      className={`p-3 rounded border ${hasOverride ? "bg-purple-500/5 border-purple-500/30" : "bg-zinc-950 border-zinc-800"}`}>
                      <div className="flex items-start gap-3">
                        <button onClick={() => toggle(f.key)} disabled={f.coming_soon}
                          data-testid={`flag-toggle-${f.key}`}
                          className={`shrink-0 mt-0.5 inline-flex items-center h-5 w-9 rounded-full p-0.5 transition-colors ${effective ? "bg-emerald-500" : "bg-zinc-700"} disabled:opacity-40 disabled:cursor-not-allowed`}>
                          <span className={`block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform ${effective ? "translate-x-4" : "translate-x-0"}`}/>
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-zinc-100">{f.title}</div>
                            {f.coming_soon && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">Bald</span>}
                            {hasOverride && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">Override</span>}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">{f.description}</div>
                        </div>
                        {hasOverride && (
                          <button onClick={() => clearOverride(f.key)}
                            data-testid={`flag-reset-${f.key}`}
                            title="Auf globalen Standard zurücksetzen"
                            className="shrink-0 text-zinc-500 hover:text-zinc-200">
                            <RefreshCw className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="tenant-flags-save"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : "Overrides speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Phase 2 Tier 2 — 6-step Onboarding Wizard
//   1. Firma (logo upload optional)
//   2. Paket / Subscription
//   3. Template (Amazon DSP / UPS / DPD / Office)
//   4. Module override (manual selection)
//   5. Initial Admin user
//   6. Review + (optional) Demo-Data + Submit
// ============================================================================
function OnboardingWizard({ packages, onClose, onSuccess }) {
  const activePackages = (packages || [])
    .filter(p => p.is_active !== false)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [logoDataUrl, setLogoDataUrl] = useState("");

  const [data, setData] = useState({
    // Step 1
    name: "",
    slug: "",
    contact_first_last: "",
    contact_email: "",
    contact_phone: "",
    contact_address: "",
    contact_country: "AT",
    primary_color: "#2563EB",
    // Step 2
    package_id: activePackages[0]?.id || "",
    // Step 3
    template_key: "office_warehouse",
    // Step 4 (manual override)
    enabled_modules_override: null,
    // Step 5
    admin_first_name: "",
    admin_last_name: "",
    admin_email: "",
    admin_password: "", // empty = auto
    // Step 6
    generate_demo_data: false,
    welcome_email_extra_note: "",
    send_welcome_email: false, // Phase 2: disabled by default — will be enabled via feature flag later
  });

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }

  // Load templates + module list once
  useEffect(() => {
    (async () => {
      try {
        const [t, m] = await Promise.all([
          api.get("/tenants/templates"),
          api.get("/modules").catch(() => ({ data: [] })),
        ]);
        setTemplates(t.data || []);
        // Build flat module list from packages + templates so override works
        // even if /modules isn't there (defensive).
        const fromPackages = new Set();
        for (const p of activePackages) (p.enabled_modules || []).forEach(x => fromPackages.add(x));
        if (Array.isArray(m.data) && m.data.length > 0) {
          setAllModules(m.data.map(x => x.key || x).filter(Boolean));
        } else {
          setAllModules([...fromPackages, "dashboard", "operations", "mitarbeiter", "fuhrpark",
            "dokumente", "finanzen", "reports", "exports", "audit", "einstellungen"]
            .filter((v, i, a) => a.indexOf(v) === i));
        }
      } catch {
        setTemplates([
          { key: "amazon_dsp", label: "Amazon DSP", description: "Last-Mile DSP" },
          { key: "ups", label: "UPS", description: "UPS Fahrer-Abrechnung" },
          { key: "dpd", label: "DPD", description: "DPD Multi-Sheet" },
          { key: "office_warehouse", label: "Büro / Lager", description: "Office/Warehouse" },
        ]);
      }
    })();
    // eslint-disable-next-line
  }, []);

  function autoSlug(name) {
    return name.toLowerCase().trim()
      .replace(/[äöüß]/g, c => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c] || c))
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  // Effective modules = override OR template defaults (calc by lookup) OR package
  const selectedPkg = activePackages.find(p => p.id === data.package_id);
  // Heuristic: from template_key look up module count from template list; for full
  // module set we trust the package because templates only return summary fields.
  const effectiveModules = useMemo(() => {
    if (Array.isArray(data.enabled_modules_override)) return data.enabled_modules_override;
    return selectedPkg?.enabled_modules || [];
  }, [data.enabled_modules_override, selectedPkg]);

  const step1Valid = data.name.trim().length >= 2 && data.slug.trim().length >= 2;
  const step2Valid = !!data.package_id;
  const step3Valid = !!data.template_key;
  const step4Valid = effectiveModules.length > 0;
  const step5Valid = data.admin_first_name && data.admin_last_name && /\S+@\S+\.\S+/.test(data.admin_email);

  function onLogoFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setLogoDataUrl(ev.target?.result || "");
    r.readAsDataURL(f);
  }

  async function submit() {
    setBusy(true); setError("");
    try {
      const payload = {
        slug: data.slug,
        name: data.name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        contact_address: data.contact_address,
        contact_country: data.contact_country,
        primary_color: data.primary_color,
        admin_first_name: data.admin_first_name,
        admin_last_name: data.admin_last_name,
        admin_email: data.admin_email,
        admin_password: data.admin_password,
        package_id: data.package_id,
        template_key: data.template_key,
        enabled_modules_override: Array.isArray(data.enabled_modules_override)
          ? data.enabled_modules_override : null,
        generate_demo_data: !!data.generate_demo_data,
        send_welcome_email: !!data.send_welcome_email,
        welcome_email_extra_note: data.welcome_email_extra_note,
      };
      const { data: res } = await api.post("/tenants/onboard", payload);

      // Optional logo upload (best-effort; silently skip on failure)
      if (logoDataUrl && res?.tenant?.id) {
        try {
          await api.post(`/tenants/${res.tenant.id}/logo`, { data_url: logoDataUrl });
        } catch (err) { console.warn("tenant logo upload skipped:", err); /* admin can re-upload later */ }
      }
      onSuccess(res);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Onboarding fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  const STEPS = [
    { n: 1, label: "Firma", icon: Building2 },
    { n: 2, label: "Paket", icon: PackageIcon },
    { n: 3, label: "Template", icon: FileText },
    { n: 4, label: "Module", icon: LayoutGrid },
    { n: 5, label: "Admin", icon: UserCog },
    { n: 6, label: "Review", icon: Check },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboard-wizard">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100 inline-flex items-center gap-2" data-testid="wizard-title">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Neuen Mandanten onboarden
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Schritt {step} von 6 — {STEPS[step - 1].label}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress stepper */}
        <div className="px-5 pt-4 pb-2 flex items-center gap-1 text-[10px] uppercase tracking-wider">
          {STEPS.map((s, i) => {
            const done = s.n < step, active = s.n === step;
            const Icon = s.icon;
            return (
              <div key={s.n} className="flex-1 flex items-center gap-1.5" data-testid={`step-indicator-${s.n}`}>
                <div className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${
                  done ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                       : active ? "bg-blue-500/20 border-blue-500/50 text-blue-300"
                       : "bg-zinc-900 border-zinc-800 text-zinc-600"
                }`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px ${done ? "bg-emerald-500/40" : "bg-zinc-800"}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="p-5 space-y-4">
          {/* ─── STEP 1 — Firma ─── */}
          {step === 1 && (
            <>
              <Field label="Firmenname *" testid="onb-name">
                <input type="text" value={data.name}
                  onChange={(e) => { setField("name", e.target.value); if (!data.slug) setField("slug", autoSlug(e.target.value)); }}
                  placeholder="z.B. Müller Transport GmbH"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
              </Field>
              <Field label="Subdomain-Slug * (URL-Identifier)" testid="onb-slug">
                <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded">
                  <input type="text" value={data.slug}
                    onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                    placeholder="mueller-transport"
                    className="flex-1 bg-transparent px-3 py-2 text-sm text-zinc-100 font-mono" />
                  <span className="px-3 text-xs text-zinc-500 font-mono border-l border-zinc-800">.damstex.com</span>
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ansprechpartner" testid="onb-contact-person">
                  <input type="text" value={data.contact_first_last}
                    onChange={(e) => setField("contact_first_last", e.target.value)}
                    placeholder="Max Mustermann"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
                </Field>
                <Field label="Kontakt-E-Mail" testid="onb-contact-email">
                  <input type="email" value={data.contact_email}
                    onChange={(e) => setField("contact_email", e.target.value)}
                    placeholder="kontakt@firma.at"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Telefon" testid="onb-phone">
                  <input type="tel" value={data.contact_phone}
                    onChange={(e) => setField("contact_phone", e.target.value)}
                    placeholder="+43 660 ..."
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
                </Field>
                <Field label="Land" testid="onb-country">
                  <select value={data.contact_country}
                    onChange={(e) => setField("contact_country", e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100">
                    <option value="AT">Österreich</option>
                    <option value="DE">Deutschland</option>
                    <option value="CH">Schweiz</option>
                    <option value="HR">Kroatien</option>
                    <option value="SI">Slowenien</option>
                  </select>
                </Field>
              </div>
              <Field label="Adresse (optional)" testid="onb-address">
                <input type="text" value={data.contact_address}
                  onChange={(e) => setField("contact_address", e.target.value)}
                  placeholder="Straße, PLZ Ort"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primärfarbe (Branding)" testid="onb-color">
                  <div className="flex items-center gap-2">
                    <input type="color" value={data.primary_color}
                      onChange={(e) => setField("primary_color", e.target.value)}
                      className="w-12 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer" />
                    <input type="text" value={data.primary_color}
                      onChange={(e) => setField("primary_color", e.target.value)}
                      className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 font-mono" />
                  </div>
                </Field>
                <Field label="Logo (optional, max 1 MB)" testid="onb-logo">
                  <input type="file" accept="image/*" onChange={onLogoFile}
                    className="w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200 file:cursor-pointer" />
                  {logoDataUrl && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={logoDataUrl} alt="logo" className="h-10 w-auto rounded bg-white p-1" />
                      <button onClick={() => setLogoDataUrl("")} className="text-xs text-zinc-500 hover:text-zinc-200">entfernen</button>
                    </div>
                  )}
                </Field>
              </div>
            </>
          )}

          {/* ─── STEP 2 — Paket ─── */}
          {step === 2 && (
            <>
              <p className="text-sm text-zinc-400 mb-3">Wählen Sie das Abo-Paket für diesen Mandanten:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {activePackages.map(p => {
                  const sel = p.id === data.package_id;
                  return (
                    <button key={p.id} type="button"
                      onClick={() => setField("package_id", p.id)}
                      data-testid={`onb-pkg-${(p.name || "").toLowerCase()}`}
                      className={`text-left rounded-lg border p-4 transition ${sel ? "border-blue-500 bg-blue-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                      <div className="font-semibold text-zinc-100 mb-1">{p.name}</div>
                      <div className="text-xs text-zinc-400 mb-3 leading-relaxed line-clamp-2">{p.description || ""}</div>
                      <div className="text-2xl font-display font-bold" style={{ color: sel ? "#60a5fa" : "#fafafa" }}>
                        {(p.price_monthly_eur || 0).toFixed(0)} €<span className="text-xs text-zinc-500 font-normal">/Mon.</span>
                      </div>
                      <div className="mt-3 space-y-1 text-[11px] text-zinc-400">
                        <div className="flex items-center justify-between"><span>Benutzer</span><span className="text-zinc-200 font-mono">{p.max_users || "∞"}</span></div>
                        <div className="flex items-center justify-between"><span>Fahrzeuge</span><span className="text-zinc-200 font-mono">{p.max_vehicles || "∞"}</span></div>
                        <div className="flex items-center justify-between"><span>Storage</span><span className="text-zinc-200 font-mono">{p.max_storage_gb ? `${p.max_storage_gb} GB` : "∞"}</span></div>
                        <div className="flex items-center justify-between"><span>Trial</span><span className="text-zinc-200 font-mono">{p.trial_days || 0} Tg</span></div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(p.enabled_modules || []).slice(0, 6).map(m => (
                          <span key={m} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{m}</span>
                        ))}
                        {(p.enabled_modules || []).length > 6 && <span className="text-[9px] text-zinc-500">+{p.enabled_modules.length - 6}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ─── STEP 3 — Template ─── */}
          {step === 3 && (
            <>
              <p className="text-sm text-zinc-400 mb-3">
                Wählen Sie ein Branchen-Template — Module, Lohn-Regeln, Abteilungen werden automatisch vorbereitet:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map(t => {
                  const sel = t.key === data.template_key;
                  return (
                    <button key={t.key} type="button"
                      onClick={() => { setField("template_key", t.key); setField("enabled_modules_override", null); }}
                      data-testid={`onb-tpl-${t.key}`}
                      className={`text-left rounded-lg border p-4 transition ${sel ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-zinc-100">{t.label}</div>
                        {sel && <Check className="w-4 h-4 text-emerald-400" />}
                      </div>
                      <div className="text-xs text-zinc-400 leading-relaxed">{t.description}</div>
                      <div className="mt-2 text-[10px] text-zinc-500">
                        {t.module_count} Module · {t.rule_group_count} Lohn-Regelgruppen
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 px-3 py-2.5 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-blue-200">
                Das Template setzt sinnvolle Defaults — Sie können im nächsten Schritt einzelne Module manuell ein-/ausschalten.
              </div>
            </>
          )}

          {/* ─── STEP 4 — Module-Override ─── */}
          {step === 4 && (
            <>
              <p className="text-sm text-zinc-400 mb-3">
                Effektive Modul-Liste: {effectiveModules.length} Module aktiv.
              </p>
              <div className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded mb-3 text-xs text-zinc-500">
                {data.enabled_modules_override === null
                  ? `Quelle: Template/Paket-Default. Klicken Sie auf "Manuell überschreiben" um einzelne Module zu (de)aktivieren.`
                  : "Manuelle Auswahl aktiv. Sie können einzelne Module umschalten oder zu Defaults zurückkehren."}
              </div>
              <div className="flex items-center gap-2 mb-3">
                {data.enabled_modules_override === null ? (
                  <button onClick={() => setField("enabled_modules_override", [...effectiveModules])}
                    data-testid="onb-modules-override-on"
                    className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200">
                    Manuell überschreiben
                  </button>
                ) : (
                  <button onClick={() => setField("enabled_modules_override", null)}
                    data-testid="onb-modules-override-reset"
                    className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-200 inline-flex items-center gap-1.5">
                    <RefreshCw className="w-3 h-3" /> Defaults wiederherstellen
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allModules.map(m => {
                  const enabled = effectiveModules.includes(m);
                  const onToggle = () => {
                    const base = Array.isArray(data.enabled_modules_override)
                      ? data.enabled_modules_override
                      : [...effectiveModules];
                    const next = enabled ? base.filter(x => x !== m) : [...new Set([...base, m])];
                    setField("enabled_modules_override", next);
                  };
                  return (
                    <button key={m} type="button" onClick={onToggle}
                      data-testid={`onb-mod-${m}`}
                      className={`text-xs px-3 py-2 rounded border transition flex items-center justify-between ${
                        enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:border-zinc-700"
                      }`}>
                      <span className="font-mono uppercase tracking-wider text-[10px]">{m}</span>
                      {enabled ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ─── STEP 5 — Initial Admin ─── */}
          {step === 5 && (
            <>
              <p className="text-sm text-zinc-400 mb-2 leading-relaxed">
                Dieser Benutzer erhält die <strong className="text-zinc-200">Admin-Rolle</strong> für den neuen Mandanten.
                Er kann später weitere Benutzer (Disponent, Fuhrparkleiter, Fahrer …) anlegen.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vorname *" testid="onb-fname">
                  <input type="text" value={data.admin_first_name}
                    onChange={(e) => setField("admin_first_name", e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
                </Field>
                <Field label="Nachname *" testid="onb-lname">
                  <input type="text" value={data.admin_last_name}
                    onChange={(e) => setField("admin_last_name", e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
                </Field>
              </div>
              <Field label="Login-E-Mail *" testid="onb-email">
                <input type="email" value={data.admin_email}
                  onChange={(e) => setField("admin_email", e.target.value)}
                  placeholder="admin@firma.at"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
              </Field>
              <Field label="Initial-Passwort (leer = automatisch generiert)" testid="onb-pw">
                <input type="text" value={data.admin_password}
                  onChange={(e) => setField("admin_password", e.target.value)}
                  placeholder="(automatisch generiert)"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 font-mono" />
                <p className="text-[11px] text-zinc-500 mt-1">Wird beim ersten Login zur Änderung gefordert.</p>
              </Field>
            </>
          )}

          {/* ─── STEP 6 — Review + Demo Data + Submit ─── */}
          {step === 6 && (
            <>
              <p className="text-sm text-zinc-400 mb-3">Bitte prüfen Sie die Eingaben:</p>
              <ReviewBlock title="Firma" testid="review-firma">
                <ReviewRow k="Name" v={data.name} />
                <ReviewRow k="Slug" v={data.slug} mono />
                <ReviewRow k="Land" v={data.contact_country} />
                <ReviewRow k="Kontakt" v={data.contact_email || data.contact_phone || "—"} />
              </ReviewBlock>
              <ReviewBlock title="Paket" testid="review-paket">
                <ReviewRow k="Paket" v={selectedPkg?.name || "—"} />
                <ReviewRow k="Preis/Monat" v={`${(selectedPkg?.price_monthly_eur || 0).toFixed(0)} €`} />
                <ReviewRow k="User-Limit" v={String(selectedPkg?.max_users ?? "∞")} />
              </ReviewBlock>
              <ReviewBlock title="Template" testid="review-template">
                <ReviewRow k="Template" v={(templates.find(t => t.key === data.template_key) || {}).label || "—"} />
              </ReviewBlock>
              <ReviewBlock title={`Module (${effectiveModules.length})`} testid="review-modules">
                <div className="flex flex-wrap gap-1 col-span-2">
                  {effectiveModules.map(m => (
                    <span key={m} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{m}</span>
                  ))}
                </div>
              </ReviewBlock>
              <ReviewBlock title="Admin-Benutzer" testid="review-admin">
                <ReviewRow k="Name" v={`${data.admin_first_name} ${data.admin_last_name}`} />
                <ReviewRow k="E-Mail" v={data.admin_email} />
                <ReviewRow k="Passwort" v={data.admin_password ? "manuell" : "wird generiert"} />
              </ReviewBlock>

              <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={data.generate_demo_data}
                    onChange={(e) => setField("generate_demo_data", e.target.checked)}
                    data-testid="onb-demo-data"
                    className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-zinc-100 inline-flex items-center gap-1.5">
                      <Database className="w-4 h-4 text-purple-400" />
                      Demo-Daten anlegen
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                      Erstellt 3 Demo-Mitarbeiter, 2 Fahrzeuge, 1 Übergabe, 1 Aufgabe und 2 Abwesenheiten.
                      Alle Datensätze sind als <span className="font-mono">is_demo=true</span> markiert
                      und können später per Knopfdruck entfernt werden.
                    </div>
                  </div>
                </label>
              </div>

              <Field label="Persönliche Notiz für Welcome-E-Mail (optional)" testid="onb-note">
                <textarea value={data.welcome_email_extra_note}
                  onChange={(e) => setField("welcome_email_extra_note", e.target.value)}
                  rows={2} placeholder="optional"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={data.send_welcome_email}
                  onChange={(e) => setField("send_welcome_email", e.target.checked)}
                  data-testid="onb-send-mail" />
                Welcome-E-Mail an <span className="font-mono text-zinc-100">{data.admin_email || "(noch keine E-Mail)"}</span> senden
              </label>
            </>
          )}

          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-400" data-testid="onb-error">
              {error}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 flex items-center justify-between sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100">Abbrechen</button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} disabled={busy} data-testid="onb-back"
                className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">← Zurück</button>
            )}
            {step < 6 ? (
              <button onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid) || (step === 3 && !step3Valid) || (step === 4 && !step4Valid) || (step === 5 && !step5Valid)}
                data-testid="onb-next"
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium inline-flex items-center gap-1.5">
                Weiter <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={busy}
                data-testid="onb-submit"
                className="px-5 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded font-medium inline-flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                {busy ? "Erstelle…" : "Mandant aktivieren"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, testid, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-1.5 text-xs">{children}</div>
    </div>
  );
}
function ReviewRow({ k, v, mono }) {
  return (
    <>
      <div className="text-zinc-500">{k}</div>
      <div className={`text-zinc-200 truncate ${mono ? "font-mono" : ""}`}>{v}</div>
    </>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function OnboardSuccessModal({ result, onClose }) {
  const [copied, setCopied] = useState("");
  const url = result.login_url?.startsWith("http") ? result.login_url
            : `${window.location.origin}${result.login_url}`;
  const copy = (key, val) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };
  const mail = result.welcome_email || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboard-success">
      <div className="bg-zinc-900 border border-emerald-500/40 rounded-lg w-full max-w-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-emerald-400"/>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-zinc-100">Mandant aktiviert!</h3>
            <p className="text-xs text-zinc-500">{result.tenant?.name} ist live.</p>
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded p-4 space-y-3 text-sm font-mono">
          <KV label="Login-URL" value={url} onCopy={() => copy("url", url)} copied={copied === "url"}/>
          <KV label="E-Mail" value={result.admin?.email} onCopy={() => copy("email", result.admin?.email)} copied={copied === "email"}/>
          <KV label="Passwort" value={result.temp_password} onCopy={() => copy("pw", result.temp_password)} copied={copied === "pw"} sensitive/>
        </div>

        <div className={`mt-4 px-3 py-2 rounded text-sm ${
          mail.status === "sent" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
          : mail.status === "failed" ? "bg-amber-500/10 border border-amber-500/30 text-amber-300"
          : "bg-zinc-800 text-zinc-400"
        }`}>
          {mail.status === "sent" && "✓ Begrüßungs-E-Mail wurde an den Admin versendet."}
          {mail.status === "failed" && `⚠ E-Mail konnte nicht gesendet werden: ${mail.error}. Bitte oben kopieren und manuell weiterleiten.`}
          {mail.status === "skipped" && "ℹ E-Mail-Versand übersprungen — bitte oben kopieren."}
        </div>

        <button onClick={onClose} data-testid="onboard-success-close"
          className="mt-5 w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded font-medium">
          Schließen
        </button>
      </div>
    </div>
  );
}

function KV({ label, value, onCopy, copied, sensitive }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
        <div className={`text-zinc-100 truncate ${sensitive ? "font-mono bg-zinc-900 px-2 py-0.5 rounded inline-block" : ""}`}>{value}</div>
      </div>
      <button onClick={onCopy} className={`text-xs px-3 py-1 rounded ${copied ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"}`}>
        {copied ? "Kopiert ✓" : "Kopieren"}
      </button>
    </div>
  );
}

// ============================================================================
// Phase 2 Tier 2 — Tenant Management Center modal
// Shows live overview + actions: suspend/reactivate, edit modules, reset
// admin password, impersonate, remove demo data.
// ============================================================================
function TenantManagementModal({ tenant, onClose, onChanged }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editingModules, setEditingModules] = useState(false);
  const [moduleList, setModuleList] = useState([]);
  // Phase 2 Tier 3 — Add-on overrides for this tenant
  const [addons, setAddons] = useState([]);
  const [catalog, setCatalog] = useState([]);

  async function loadOverview() {
    setLoading(true);
    try {
      const [{ data: ov }, { data: ents }, { data: cat }] = await Promise.all([
        api.get(`/tenants/${tenant.id}/overview`),
        api.get(`/tenants/${tenant.id}/addons`),
        api.get(`/extensions/catalog`),
      ]);
      setOverview(ov);
      setModuleList(ov?.tenant?.enabled_modules || []);
      setAddons(ents || []);
      setCatalog(cat || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übersicht konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadOverview(); /* eslint-disable-next-line */ }, []);

  async function toggleAddon(ext, newStatus) {
    setBusy(`addon-${ext.key}`);
    try {
      if (newStatus === "revoke") {
        await api.delete(`/tenants/${tenant.id}/addons/${ext.key}`);
        toast.success(`${ext.name} entzogen`);
      } else {
        await api.put(`/tenants/${tenant.id}/addons`, {
          extension_key: ext.key, status: newStatus, included_by_contract: false,
        });
        toast.success(`${ext.name} → ${newStatus}`);
      }
      await loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(""); }
  }

  const ALL_MODULES = [
    "dashboard", "operations", "mitarbeiter", "fuhrpark",
    "dokumente", "finanzen", "exports", "reports", "audit", "einstellungen",
  ];

  async function saveModules() {
    setBusy("modules");
    try {
      await api.put(`/tenants/${tenant.id}/modules`, { enabled_modules: moduleList });
      toast.success("Module gespeichert");
      setEditingModules(false);
      await loadOverview();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(""); }
  }

  async function resetPassword() {
    if (!window.confirm(`Passwort des Admin-Benutzers für "${tenant.name}" zurücksetzen?\n\nEin neues Passwort wird sofort angezeigt.`)) return;
    setBusy("pw");
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/reset-admin-password`, {});
      window.prompt(`Neues Passwort für ${data.user_email} (jetzt kopieren!):`, data.temp_password);
      toast.success("Passwort zurückgesetzt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    } finally { setBusy(""); }
  }

  async function impersonate() {
    if (!window.confirm(`Als Admin von "${tenant.name}" anmelden?\n\nIhre aktuelle Super-Admin-Sitzung wird für 30 Minuten durch die Tenant-Admin-Sitzung ersetzt. Ein Sicherheits-Banner am oberen Bildschirmrand erlaubt jederzeit die Rückkehr.`)) return;
    setBusy("imp");
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/impersonate`);
      // Phase 5Z.19f — Impersonation Safety Bar:
      // 1. Save the current super-admin token so we can restore it later.
      // 2. Save metadata describing the active impersonation (start, expiry, names).
      // 3. Swap the active bearer token to the impersonated one.
      try {
        const currentToken = localStorage.getItem("mt_token");
        if (currentToken) localStorage.setItem("mt_token_super_origin", currentToken);
        const ttlMin = Number(data.expires_in_minutes || 30);
        const startedAt = Date.now();
        const expiresAt = startedAt + ttlMin * 60 * 1000;
        localStorage.setItem(
          "mt_impersonation",
          JSON.stringify({
            started_at: startedAt,
            expires_at: expiresAt,
            target_user_email: data.user?.email || "",
            target_user_name: data.user?.name || "",
            target_tenant_id: data.tenant?.id || "",
            target_tenant_name: data.tenant?.name || "",
            origin_email: data.origin?.email || "",
          }),
        );
        localStorage.setItem("mt_token", data.access_token);
      } catch (err) { console.warn("impersonation localStorage write failed:", err); /* ignore — cookie auth still works */ }
      toast.success(`Als ${data.user.email} angemeldet — wird neu geladen…`);
      setTimeout(() => { window.location.href = "/"; }, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation fehlgeschlagen");
      setBusy("");
    }
  }

  async function removeDemo() {
    if (!window.confirm("Alle Demo-Daten dieses Mandanten löschen?")) return;
    setBusy("demo");
    try {
      const { data } = await api.delete(`/tenants/${tenant.id}/demo-data`);
      toast.success(`Demo-Daten entfernt: ${data.employees}+${data.vehicles}+${data.handovers}+${data.tasks}+${data.absences} Datensätze`);
      await loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setBusy(""); }
  }

  function fmtBytes(b) {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tenant-manage-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100 inline-flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-400" />
              {tenant.name}
            </h3>
            <p className="text-xs text-zinc-500 mt-1 font-mono">{tenant.slug}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="manage-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="text-zinc-500 text-sm">Übersicht wird geladen…</div>
          ) : !overview ? (
            <div className="text-amber-400 text-sm">Übersicht nicht verfügbar.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Status" value={overview.tenant.status} testid="manage-stat-status"
                  cls={overview.tenant.status === "active" ? "text-emerald-400" : overview.tenant.status === "suspended" ? "text-amber-400" : "text-zinc-300"} />
                <MiniStat label="Paket" value={overview.package?.name || overview.tenant.plan || "—"} testid="manage-stat-pkg" />
                <MiniStat label="Benutzer" value={overview.counts.users} testid="manage-stat-users" />
                <MiniStat label="Storage" value={fmtBytes(overview.storage_bytes)} testid="manage-stat-storage" />
                <MiniStat label="Mitarbeiter" value={overview.counts.employees} testid="manage-stat-emp" />
                <MiniStat label="Fahrzeuge" value={overview.counts.vehicles} testid="manage-stat-veh" />
                <MiniStat label="Übergaben" value={overview.counts.handovers} testid="manage-stat-ho" />
                <MiniStat label="Demo-Daten" value={overview.counts.demo_employees ? `${overview.counts.demo_employees} MA` : "—"} testid="manage-stat-demo" />
              </div>

              {overview.last_activity && (
                <div className="text-xs text-zinc-500">
                  Letzte Aktivität: <span className="text-zinc-300 font-mono">{fmtDateTime(overview.last_activity)}</span>
                </div>
              )}

              <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="manage-modules-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                    <LayoutGrid className="w-3.5 h-3.5" /> Aktive Module ({moduleList.length})
                  </div>
                  {!editingModules ? (
                    <button onClick={() => setEditingModules(true)} data-testid="manage-modules-edit"
                      className="text-xs text-blue-400 hover:text-blue-300">Bearbeiten</button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingModules(false); setModuleList(overview.tenant.enabled_modules || []); }}
                        className="text-xs text-zinc-400 hover:text-zinc-200">Abbrechen</button>
                      <button onClick={saveModules} disabled={busy === "modules"}
                        data-testid="manage-modules-save"
                        className="text-xs px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded">
                        {busy === "modules" ? "Speichert…" : "Speichern"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_MODULES.map(m => {
                    const enabled = moduleList.includes(m);
                    const onClick = () => {
                      if (!editingModules) return;
                      setModuleList(prev => enabled ? prev.filter(x => x !== m) : [...new Set([...prev, m])]);
                    };
                    return (
                      <button key={m} type="button" onClick={onClick}
                        disabled={!editingModules}
                        data-testid={`manage-mod-${m}`}
                        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${
                          enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                                  : "border-zinc-800 bg-zinc-950 text-zinc-500"
                        } ${editingModules ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Add-ons / Extensions overrides */}
              <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="manage-addons-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Add-ons ({addons.filter(a => a.status !== "disabled").length} aktiv)
                  </div>
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {catalog.filter(c => c.active).map(c => {
                    const ent = addons.find(a => a.extension_key === c.key);
                    const isActive = ent && ent.status === "active";
                    const isTrial = ent && ent.status === "trial";
                    const isContract = ent?.included_by_contract;
                    return (
                      <div key={c.key} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-xs"
                        data-testid={`manage-addon-${c.key}`}>
                        <span className="flex-1 text-zinc-200 truncate">{c.name}</span>
                        {isContract && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">Vertrag</span>}
                        {isTrial && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">Trial</span>}
                        {isActive && !isContract && !isTrial && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">Aktiv</span>}
                        {ent?.status === "disabled" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">Deaktiviert</span>}
                        {!ent && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">Nicht aktiv</span>}
                        <div className="flex gap-1">
                          {!isActive && (
                            <button onClick={() => toggleAddon(c, "active")} disabled={busy === `addon-${c.key}`}
                              data-testid={`addon-activate-${c.key}`}
                              className="text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded">Aktivieren</button>
                          )}
                          {!isTrial && !isActive && c.trial_enabled && (
                            <button onClick={() => toggleAddon(c, "trial")} disabled={busy === `addon-${c.key}`}
                              data-testid={`addon-trial-${c.key}`}
                              className="text-[10px] px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded">Trial</button>
                          )}
                          {(isActive || isTrial) && !isContract && (
                            <button onClick={() => toggleAddon(c, "revoke")} disabled={busy === `addon-${c.key}`}
                              data-testid={`addon-revoke-${c.key}`}
                              className="text-[10px] px-2 py-0.5 bg-zinc-800 hover:bg-red-500/30 text-zinc-300 rounded">Entziehen</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <ActionButton testid="manage-reset-pw" icon={KeyRound} color="amber"
                  label="Admin-Passwort zurücksetzen"
                  hint="Neues Temp-Passwort wird einmalig angezeigt."
                  onClick={resetPassword} disabled={busy === "pw"} />
                <ActionButton testid="manage-impersonate" icon={UserCog} color="blue"
                  label="Als Mandanten-Admin anmelden"
                  hint="30-Min-Token. Sie können sich danach neu anmelden."
                  onClick={impersonate} disabled={busy === "imp"} />
                {overview.counts.demo_employees > 0 && (
                  <ActionButton testid="manage-remove-demo" icon={Database} color="purple"
                    label="Demo-Daten entfernen"
                    hint={`${overview.counts.demo_employees} Demo-MA + zugehörige Datensätze`}
                    onClick={removeDemo} disabled={busy === "demo"} />
                )}
                {!tenant.is_legacy && (
                  <ActionButton testid="manage-suspend"
                    icon={tenant.status === "suspended" ? Play : Pause}
                    color={tenant.status === "suspended" ? "emerald" : "amber"}
                    label={tenant.status === "suspended" ? "Mandant reaktivieren" : "Mandant sperren"}
                    hint={tenant.status === "suspended" ? "Login-Sperre aufheben." : "Login-Sperre aktivieren — reversibel."}
                    onClick={async () => {
                      const target = tenant.status === "suspended" ? "reactivate" : "suspend";
                      try {
                        await api.post(`/tenants/${tenant.id}/${target}`);
                        toast.success(target === "suspend" ? "Mandant gesperrt" : "Mandant reaktiviert");
                        await loadOverview(); onChanged?.();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
                      }
                    }}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 flex justify-end sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Schließen</button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, testid, cls }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</div>
      <div className={`text-sm font-display font-semibold truncate ${cls || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function ActionButton({ label, hint, icon: Icon, color = "zinc", onClick, disabled, testid }) {
  const colorMap = {
    zinc:    "border-zinc-800 hover:bg-zinc-800 text-zinc-200",
    blue:    "border-blue-500/30 hover:bg-blue-500/10 text-blue-200",
    amber:   "border-amber-500/30 hover:bg-amber-500/10 text-amber-200",
    emerald: "border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-200",
    purple:  "border-purple-500/30 hover:bg-purple-500/10 text-purple-200",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`text-left p-3 rounded border bg-zinc-950 transition disabled:opacity-50 disabled:cursor-not-allowed ${colorMap[color] || colorMap.zinc}`}>
      <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-medium">{label}</div>
          {hint && <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{hint}</div>}
        </div>
      </div>
    </button>
  );
}

